if ( typeof StoreSelector !== 'function' ) {

  /* IVIN: the header's store line ("Mi tienda | Cierra a las 18:00") is computed from the store's opening hours in
     SHOP time, not picked from a per-weekday sentence by the visitor's device clock. It was: a visitor in California
     at 23:10 on Monday (00:10 Tuesday in Puerto Vallarta, shop closed) read "Closes at 6 pm".
     Hours come from the store block (sections/store-selector.liquid: "ivin_hours", same format as ivin-ops
     SHOP_HOURS: "1-5 10:00-18:00; 6 10:00-16:00", 0 = Sunday; days not listed are closed) and its time zone
     ("store_zone"). The sentences come from the locale files via data attributes on <store-selector>.
     The pure functions between pure:start/end have no DOM and no clock (tested in node with fixed times). */
  /* pure:start */
  const ivinParseHours = (spec) => {
    const table = {};
    String(spec || '').split(';').forEach(part => {
      const m = part.trim().match(/^([0-6](?:\s*[-,]\s*[0-6])*)\s+(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})$/);
      if ( ! m ) return;
      const open = Number(m[2]) * 60 + Number(m[3]);
      const close = Number(m[4]) * 60 + Number(m[5]);
      if ( close <= open ) return;
      m[1].replace(/\s/g, '').split(',').forEach(range => {
        const [a, b] = range.split('-').map(Number);
        for ( let d = a; d <= (isNaN(b) ? a : b); d++ ) table[d] = [open, close];
      });
    });
    return table;
  };
  // wd: 0 = Sunday … 6 = Saturday; mins: minutes since midnight, shop time.
  // Returns { state: 'open', t: closing } | { state: 'opens', day: days from today (0 = today), wd, t: opening } | null
  const ivinStoreStatus = (table, wd, mins) => {
    const today = table[wd];
    if ( today && mins >= today[0] && mins < today[1] ) return { state: 'open', t: today[1] };
    for ( let day = 0; day < 8; day++ ) {
      const d = (wd + day) % 7;
      const h = table[d];
      if ( h && ( day > 0 || mins < h[0] ) ) return { state: 'opens', day, wd: d, t: h[0] };
    }
    return null;
  };
  // 24 h "18:00" or 12 h "6 pm" / "6:30 pm"
  const ivinClockText = (t, twelve) => {
    const h = Math.floor(t / 60), m = t % 60;
    if ( ! twelve ) return `${h}:${String(m).padStart(2, '0')}`;
    return `${h % 12 || 12}${m ? `:${String(m).padStart(2, '0')}` : ''} ${h < 12 ? 'am' : 'pm'}`;
  };
  // s: { closes, opensToday, opensTomorrow, opensOn, days: [7 names], twelve } with %time% / %day% placeholders
  const ivinStoreStatusText = (status, s) => {
    if ( ! status ) return '';
    const time = ivinClockText(status.t, s.twelve);
    let text;
    if ( status.state === 'open' ) text = s.closes;
    else if ( status.day === 0 ) text = s.opensToday;
    else if ( status.day === 1 ) text = s.opensTomorrow;
    else text = s.opensOn.replace('%day%', s.days[status.wd] || '');
    return text.replace('%time%', time);
  };
  /* pure:end */
  // weekday + minutes since midnight in the given IANA time zone, whatever the visitor's device is set to
  const ivinZoneNow = (zone) => {
    const parts = new Intl.DateTimeFormat('en-GB', { timeZone: zone, weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(new Date());
    const get = type => parts.find(p => p.type === type).value;
    return {
      wd: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(get('weekday')),
      mins: (Number(get('hour')) % 24) * 60 + Number(get('minute'))
    };
  };

  class StoreSelector extends HTMLElement {

    constructor() {

      super();

      this.mainSelector = this.hasAttribute('data-main-selector');
      this.storeSelectorButton = this.mainSelector ? document.querySelector('[data-js-select-store]') : null;
      this.canChangeStore = true;

      const interactiveMap = document.getElementById(this.dataset.mapSelector);

      if ( this.mainSelector && this.hasAttribute('data-single-store') ) {
        localStorage.setItem('selected-store', this.querySelector('.store-selector-item').getAttribute('data-handle'));
      }
      const selectedStore = localStorage.getItem('selected-store');
      let selectedStoreFound = false;
      
      this.storesList = [];
      this.querySelectorAll('.store-selector-item').forEach(elm=>{

        const item = elm;
        this.storesList[item.dataset.handle] = item.dataset.title;

        if ( selectedStore && selectedStore === item.getAttribute('data-handle') ) {

          this.#setSelectedStore(item, item);
          this.#changeSelectedStoreHandleContent(selectedStore);
          selectedStoreFound = true;

          if ( interactiveMap ) {
            interactiveMap.addEventListener('ready', ()=>{
              setTimeout(()=>{
                interactiveMap.triggerMarkerClick(item.dataset.handle);
              }, 1000);
            })
          }

        }

        if ( ! this.hasAttribute('data-single-store') ) {

          const toggleEl = document.createElement('div');
          toggleEl.classList = `js-accordion-tab-toggle ${this.mainSelector ? 'js-accordion-tab-toggle--main' : ''}`;
          toggleEl.tabIndex = 0;
          toggleEl.innerHTML = KROWN.settings.symbols.toggle_pack_alternate;
          item.querySelector('.js-accordion-tab').appendChild(toggleEl);

          if ( this.mainSelector ) {
            const changeStoreEv = e=>{
              this.#clearSelectedStore(item);
              this.#setSelectedStore(item, e.target);
              if ( interactiveMap ) {
                if ( item.classList.contains('store-selector-item--store--active') ) {
                  interactiveMap.triggerMarkerClick(item.dataset.handle);
                }
              }
            }
            item.querySelector('.store-selector-item__input-container').addEventListener('click', changeStoreEv);
            item.querySelector('.store-selector-item__input-container').addEventListener('keyup', e=>{
              if ( e.keyCode === window.KEYCODES.RETURN ) {
                changeStoreEv(e);
              }
            });
          }

          const openAccordionEv = () => {
            this.#openStoreTab(item);
            this.canChangeStore = false;

            if ( interactiveMap ) {
              if ( item.classList.contains('store-selector-item--active') ) {
                interactiveMap.triggerMarkerClick(item.dataset.handle);
              }
            }

            if ( item.querySelector('css-slider') ) {
              item.querySelector('css-slider').resetSlider();
            }
          }

          item.querySelector(`${this.mainSelector ? '.js-accordion-tab-toggle' : '.js-accordion-tab'}`).addEventListener('click', openAccordionEv);
          item.querySelector(`${this.mainSelector ? '.js-accordion-tab-toggle' : '.js-accordion-tab'}`).addEventListener('keyup', e=>{
            if ( e.keyCode === window.KEYCODES.RETURN ) {
              openAccordionEv();
            }
          });

        }

      });

      if ( this.storeSelectorButton && selectedStoreFound === false ) {
        this.storeSelectorButton.classList.add('show-select-label');
      }

      if ( interactiveMap ) {
        interactiveMap.addEventListener('marker', (e)=>{
          if ( this.canChangeStore ) {
            const item = this.querySelector(`.store-selector-item[data-handle="${e.detail.store}"]`);
            if ( ! item.classList.contains('store-selector-item--store--active' ) ) {
              this.#clearSelectedStore(item);
              this.#setSelectedStore(item, item);
            }
          } else {
            this.canChangeStore = true;
          }
        });
      }
      
      if ( document.querySelector('[data-js-select-store]') ) {
        const storeChanged = new CustomEvent('storechanged');
        document.querySelector('[data-js-select-store]').addEventListener('click', e=>{
          e.preventDefault();
          if ( this.querySelector('.store-selector-item--store--active') ) {
            const selectedStoreNew = this.querySelector('.store-selector-item--store--active').getAttribute('data-handle');
            localStorage.setItem('selected-store', selectedStoreNew);
            this.#changeSelectedStoreHandleContent(selectedStoreNew);
          } else {
            localStorage.removeItem('selected-store');
            this.#changeSelectedStoreHandleContent();
          }
          this.dispatchEvent(storeChanged);
        });
      }

    }

    #openStoreTab(item) {
      const open = item.classList.contains('store-selector-item--active') ? false : true;
      this.querySelectorAll('.store-selector-item--active').forEach(elm=>{
        this.#slideUp(elm.querySelector('[role=tabpanel]'), 200);
        elm.classList.remove('store-selector-item--active');
      });
      if ( open ) {
        item.classList.add('store-selector-item--active');
        this.#slideDown(item.querySelector('[role=tabpanel]'), 200);
      }
    }
    
    #clearSelectedStore(item) {
      if ( ! item.classList.contains('store-selector-item--store--active') && this.querySelector('.store-selector-item--store--active') ) {
        const activeInput = this.querySelector('.store-selector-item--store--active input');
        if ( activeInput ) {
          activeInput.checked = false;
        }
        this.querySelector('.store-selector-item--store--active').classList.remove('store-selector-item--store--active');
      }
    }

    #setSelectedStore(item, button) {
      item.classList.toggle('store-selector-item--store--active');
      if ( this.mainSelector ) {
        item.querySelector('input').checked = !item.querySelector('input').checked;
        button.setAttribute('aria-selected', button.getAttribute('aria-selected') === "true" ? "false" : "true");
        if ( this.storeSelectorButton ) {
          if ( item.classList.contains('store-selector-item--store--active') ) {
            this.storeSelectorButton.style.display = "block";
          } else {
            this.storeSelectorButton.style.display = "none";
          }
        }
      }
    }

    // "Cierra a las 18:00" / "Abre hoy a las 10:00" / "Abre mañana a las 10:00" / "Abre el lunes a las 10:00";
    // '' when the store has no hours or time zone (the line then shows only "Mi tienda")
    #storeStatusText(store) {
      const item = this.querySelector(`.store-selector-item[data-handle="${store}"]`);
      if ( ! item || ! item.dataset.ivinHours || ! item.dataset.ivinZone ) return '';
      const table = ivinParseHours(item.dataset.ivinHours);
      if ( Object.keys(table).length === 0 ) return '';
      let now;
      try { now = ivinZoneNow(item.dataset.ivinZone); } catch (e) { return ''; } // unknown zone / old browser: no status rather than a wrong one
      const d = this.dataset;
      return ivinStoreStatusText(ivinStoreStatus(table, now.wd, now.mins), {
        closes: d.ivinCloses || '', opensToday: d.ivinOpensToday || '', opensTomorrow: d.ivinOpensTomorrow || '',
        opensOn: d.ivinOpensOn || '', days: (d.ivinDays || '').split(','), twelve: d.ivinClock === '12'
      });
    }

    #changeSelectedStoreHandleContent(store = null) {

      // keep the open/closed line current while the page stays open (17:59 → 18:00 flips without a reload)
      clearInterval(this._ivinStatusTimer);
      if ( this.mainSelector && store ) {
        this._ivinStatusTimer = setInterval(() => this.#changeSelectedStoreHandleContent(store), 60000);
      }

      
      if ( this.mainSelector ) {

        document.querySelectorAll('[data-type="store-selector"]').forEach(storeSelectorHandle=>{

          if ( store ) {

            // IVIN: open/closed state from the store's hours in shop time (see ivinStoreStatus above)
            const storeStatus = this.#storeStatusText(store);
            storeSelectorHandle.querySelector('[data-store-title]').textContent = `${KROWN.settings.locales.store_selector_title_selected}${storeStatus ? `\u00a0|\u00a0${storeStatus}` : ''}`;
            storeSelectorHandle.querySelector('[data-store-label]').textContent = this.storesList[store];

            if ( this.storeSelectorButton ) {
              this.storeSelectorButton.classList.add('show-change-label');
              this.storeSelectorButton.classList.remove('show-select-label');
            }

          } else {

            storeSelectorHandle.querySelector('[data-store-title]').textContent = KROWN.settings.locales.store_selector_title_default;
            storeSelectorHandle.querySelector('[data-store-label]').textContent = KROWN.settings.locales.store_selector_label;

          }

        })

      }

    }

		#slideUp(target, duration){
			target.style.transitionProperty = 'height, margin, padding';
			target.style.transitionDuration = duration + 'ms';
			target.style.boxSizing = 'border-box';
			target.style.height = target.offsetHeight + 'px';
			target.offsetHeight;
			target.style.overflow = 'hidden';
			target.style.height = 0;
			target.style.paddingTop = 0;
			target.style.paddingBottom = 0;
			target.style.marginTop = 0;
			target.style.marginBottom = 0;
			setTimeout(()=>{
				target.style.display = 'none';
				target.style.removeProperty('height');
				target.style.removeProperty('padding-top');
				target.style.removeProperty('padding-bottom');
				target.style.removeProperty('margin-top');
				target.style.removeProperty('margin-bottom');
				target.style.removeProperty('overflow');
				target.style.removeProperty('transition-duration');
				target.style.removeProperty('transition-property');
			}, duration);
		}

		#slideDown(target, duration) {
			target.style.removeProperty('display');
			var display = window.getComputedStyle(target).display;

			if (display === 'none')
				display = 'block';

			target.style.display = display;
			var height = target.offsetHeight;
			target.style.overflow = 'hidden';
			target.style.height = 0;
			target.style.paddingTop = 0;
			target.style.paddingBottom = 0;
			target.style.marginTop = 0;
			target.style.marginBottom = 0;
			target.offsetHeight;
			target.style.boxSizing = 'border-box';
			target.style.transitionProperty = "height, margin, padding";
			target.style.transitionDuration = duration + 'ms';
			target.style.height = height + 'px';
			target.style.removeProperty('padding-top');
			target.style.removeProperty('padding-bottom');
			target.style.removeProperty('margin-top');
			target.style.removeProperty('margin-bottom');
			setTimeout(()=>{
				target.style.removeProperty('height');
				target.style.removeProperty('overflow');
				target.style.removeProperty('transition-duration');
				target.style.removeProperty('transition-property');
			}, duration);
		}

  }

  if ( typeof customElements.get('store-selector') === 'undefined' ) {
    customElements.define('store-selector', StoreSelector);
  }

}